/**
 * Inline search dropdown for the Allora two-tier header (desktop only).
 *
 * Fetches rendered HTML from sections/header-search-dropdown.liquid via the Section Rendering API,
 * so Shopify's predictive-search pipeline and the section's B2B gating are reused as-is.
 *
 * Below the desktop breakpoint this stays inert and Focal's own search drawer handles things — the
 * drawer is the better pattern on a phone.
 */
(() => {
  const BREAKPOINT = '(min-width: 1200px)';
  const MIN_CHARS = 2;
  const DEBOUNCE_MS = 250;

  /**
   * Fetches rendered results from sections/header-search-dropdown.liquid.
   * limit_scope=each applies the limit per resource type rather than across the total.
   */
  async function fetchResults(term, signal) {
    // `query` must be included or predictive_search.resources.queries comes back empty and the
    // Suggestions column disappears. limit_scope=each applies the limit per type, not to the total.
    const params = new URLSearchParams({
      q: term,
      'resources[type]': 'product,collection,query',
      'resources[limit]': '6',
      'resources[limit_scope]': 'each',
      section_id: 'header-search-dropdown'
    });

    const response = await fetch(
      `${window.themeVariables.routes.predictiveSearchUrl}?${params}`,
      { signal }
    );

    if (!response.ok) throw new Error(response.statusText);

    // The response is wrapped in the section's own div; unwrap it.
    const holder = document.createElement('div');
    holder.innerHTML = await response.text();
    return holder.firstElementChild ? holder.firstElementChild.innerHTML : '';
  }

  class HeaderSearchDropdown {
    constructor(root) {
      this.root = root;
      this.wrapper = root.querySelector('[data-header-search-dropdown]');
      this.results = root.querySelector('[data-hsd-results]');
      this.loading = root.querySelector('[data-hsd-loading]');
      this.input = root.querySelector('.predictive-search__input');
      this.form = root.querySelector('.predictive-search__form');

      if (!this.wrapper || !this.results || !this.input) return;

      this.media = window.matchMedia(BREAKPOINT);
      this.controller = null;
      this.timer = null;
      this.lastTerm = '';

      this.onInput = this.onInput.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onMediaChange = this.onMediaChange.bind(this);

      // The header collapse re-lays-out the rows; an open dropdown would hold its row unclipped
      // and both rows would render at once mid-transition.
      this.onHeaderCollapse = this.close.bind(this);
      document.addEventListener('header:collapse', this.onHeaderCollapse);

      this.media.addEventListener('change', this.onMediaChange);
      this.onMediaChange();
    }

    /**
     * Focal's PredictiveSearchInput opens its drawer on click via aria-controls. On desktop that
     * attribute is removed so the two do not both fire; it is restored below the breakpoint.
     */
    onMediaChange() {
      if (this.media.matches) {
        this.drawerTarget = this.drawerTarget || this.input.getAttribute('aria-controls');
        this.input.removeAttribute('aria-controls');
        this.input.addEventListener('input', this.onInput);
        this.input.addEventListener('keydown', this.onKeydown);
        document.addEventListener('click', this.onDocumentClick);
      } else {
        if (this.drawerTarget) this.input.setAttribute('aria-controls', this.drawerTarget);
        this.input.removeEventListener('input', this.onInput);
        this.input.removeEventListener('keydown', this.onKeydown);
        document.removeEventListener('click', this.onDocumentClick);
        this.close();
      }
    }

    onInput() {
      const term = this.input.value.trim();
      clearTimeout(this.timer);

      if (term.length < MIN_CHARS) {
        this.close();
        return;
      }

      this.timer = setTimeout(() => this.search(term), DEBOUNCE_MS);
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        this.input.blur();
      }
    }

    onDocumentClick(event) {
      if (!this.root.contains(event.target)) this.close();
    }

    async search(term) {
      if (term === this.lastTerm && !this.wrapper.hidden) return;
      this.lastTerm = term;

      if (this.controller) this.controller.abort();
      const controller = new AbortController();
      this.controller = controller;

      this.wrapper.hidden = false;

      // Spinner only when there is nothing to show yet; later searches swap content in place so
      // the panel does not flicker on every keystroke.
      const isFirstFetch = this.results.innerHTML.trim() === '';
      this.loading.hidden = !isFirstFetch;

      try {
        this.results.innerHTML = await fetchResults(term, controller.signal);
      } catch (error) {
        if (error.name !== 'AbortError') this.close();
      } finally {
        // Runs on abort too, so a superseded request cannot leave the spinner on screen.
        if (this.controller === controller) this.loading.hidden = true;
      }
    }

    close() {
      clearTimeout(this.timer);
      if (this.controller) {
        this.controller.abort();
        this.controller = null;
      }
      this.wrapper.hidden = true;
      this.loading.hidden = true;
      // Cleared so a reopen does not flash the previous term's results.
      this.results.innerHTML = '';
      this.lastTerm = '';
    }

    /** Called on section unload — the theme editor re-renders sections as new nodes. */
    destroy() {
      if (!this.media) return;
      this.close();
      document.removeEventListener('header:collapse', this.onHeaderCollapse);
      this.media.removeEventListener('change', this.onMediaChange);
      this.input.removeEventListener('input', this.onInput);
      this.input.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('click', this.onDocumentClick);
    }
  }

  /**
   * Mobile drawer. Shares the same section as the desktop dropdown so both show one view rather
   * than tabs. Open/close, overlay and scroll locking come from the theme's <drawer-content>.
   */
  class HeaderSearchMobile {
    constructor(root) {
      this.root = root;
      this.results = root.querySelector('[data-hsm-results]');
      this.loading = root.querySelector('[data-hsm-loading]');
      this.input = root.querySelector('[name="q"]');

      if (!this.results || !this.input) return;

      this.controller = null;
      this.timer = null;

      this.onInput = this.onInput.bind(this);
      this.input.addEventListener('input', this.onInput);
    }

    onInput() {
      const term = this.input.value.trim();
      clearTimeout(this.timer);

      if (term.length < MIN_CHARS) {
        this.results.innerHTML = '';
        this.loading.hidden = true;
        if (this.controller) {
          this.controller.abort();
          this.controller = null;
        }
        return;
      }

      this.timer = setTimeout(() => this.search(term), DEBOUNCE_MS);
    }

    destroy() {
      if (!this.input) return;
      clearTimeout(this.timer);
      if (this.controller) this.controller.abort();
      this.input.removeEventListener('input', this.onInput);
    }

    async search(term) {
      if (this.controller) this.controller.abort();
      const controller = new AbortController();
      this.controller = controller;

      this.loading.hidden = this.results.innerHTML.trim() !== '';

      try {
        this.results.innerHTML = await fetchResults(term, controller.signal);
      } catch (error) {
        if (error.name !== 'AbortError') this.results.innerHTML = '';
      } finally {
        if (this.controller === controller) this.loading.hidden = true;
      }
    }
  }

  // Tracked so listeners can be torn down when the theme editor re-renders the section.
  const instances = new Set();

  const init = () => {
    document.querySelectorAll('.header-tt__search').forEach((root) => {
      if (!root.dataset.hsdReady) {
        root.dataset.hsdReady = 'true';
        instances.add(new HeaderSearchDropdown(root));
      }
    });

    document.querySelectorAll('.hsm').forEach((root) => {
      if (!root.dataset.hsmReady) {
        root.dataset.hsmReady = 'true';
        instances.add(new HeaderSearchMobile(root));
      }
    });
  };

  const destroyDetached = () => {
    instances.forEach((instance) => {
      if (!instance.root || instance.root.isConnected) return;
      if (typeof instance.destroy === 'function') instance.destroy();
      instances.delete(instance);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', () => {
    destroyDetached();
    init();
  });

  document.addEventListener('shopify:section:unload', destroyDetached);
})();
